import { useUser } from "~/composables/useUser";

/**
 * Upsell funnel events pushed to the GTM dataLayer (container in nuxt.config).
 * GA4 tags for the `upsell_*` events are configured in the GTM container, not
 * in this repo.
 *
 * - gate_view        a Pro gate notice was shown
 * - gate_click       its call-to-action was pressed
 * - credit_block     the user was stopped (or warned) because of credits
 * - checkout_open    a Stripe checkout session was created and opened
 * - checkout_success Stripe redirected back after a completed checkout
 */
export type UpsellEvent =
  | "gate_view"
  | "gate_click"
  | "credit_block"
  | "checkout_open"
  | "checkout_success";

export type UpsellGate = "guest" | "trial" | "upgrade";
export type UpsellModal = "subscription" | "payPerUse" | "lifetime";

export type UpsellPayload = {
  feature?: string | null;
  gate?: UpsellGate;
  modal?: UpsellModal | string;
  plan?: string;
  // The account still reads as free right after checkout, so the page passes
  // the interval it was sent back with.
  interval?: string;
};

// View-type events fire once per (event, feature, gate) per page view so
// re-renders and reactive toggles do not inflate the counts.
const seenThisPage = new Set<string>();
let seenPath: string | null = null;

const ONCE_PER_PAGE: UpsellEvent[] = ["gate_view", "credit_block"];

export const useUpsellTracking = () => {
  const { user } = useUser();
  const route = useRoute();

  const trackUpsell = (event: UpsellEvent, payload: UpsellPayload = {}) => {
    if (!import.meta.client) return;
    try {
      if (ONCE_PER_PAGE.includes(event)) {
        if (seenPath !== route.path) {
          seenThisPage.clear();
          seenPath = route.path;
        }
        const key = `${event}:${payload.feature ?? ""}:${payload.gate ?? ""}`;
        if (seenThisPage.has(key)) return;
        seenThisPage.add(key);
      }

      useGtm()?.trackEvent({
        event: `upsell_${event}`,
        feature: payload.feature ?? undefined,
        gate: payload.gate,
        modal: payload.modal,
        plan: payload.plan,
        plan_state: user.value?.planState ?? "guest",
        billing_interval: payload.interval ?? user.value?.billingInterval ?? undefined,
        page_path: route.path,
      });
    } catch {
      // Analytics must never break the product flow.
    }
  };

  return { trackUpsell };
};
